import { computed, Ref, ref, watch } from 'vue'
import { orderBy } from 'lodash'
import { SearchOptions } from '@/components/vision/next/inputs/UseNewAutocomplete'
import { PickerItemType } from '@/components/vision/next/picker-list/models'
import { useInventoryTypesWithResourceTypeActivity } from '@/composables/inventory-types/useInventoryTypes'

export function useJobInventoryPicker(
  resourceTypeActivityId: Ref<number | null>,
  disableInventorySubTypes?: Ref<number[]>,
) {
  const {
    allInventoryTypes,
    filteredInventoryTypesByResourceTypeActivityId,
    isFetched,
    isFetching,
    isInitialLoading,
  } = useInventoryTypesWithResourceTypeActivity(resourceTypeActivityId)

  const allFilteredInventorySubTypes = computed(() =>
    filteredInventoryTypesByResourceTypeActivityId.value.flatMap((x) => x.inventorySubTypes || []),
  )

  const allInventorySubTypes = computed(
    () => allInventoryTypes.value?.flatMap((x) => x.inventorySubTypes || []) ?? [],
  )

  const searchInventories = (options: Ref<SearchOptions>) => {
    const items = computed<Readonly<PickerItemType[]>>(() => {
      const searchText = options.value.searchText?.toLowerCase() ?? ''

      // Case 1: No search text + no parent → all parent types
      if (searchText === '' && !options.value.parentId) {
        const inventoryTypes = filteredInventoryTypesByResourceTypeActivityId.value.map((t) => ({
          id: t.id,
          title: t.name,
          selectable: false,
          expandable: t.inventorySubTypes.length > 0,
        }))
        return orderBy(inventoryTypes, [(x) => x.title?.toLowerCase()])
      }

      // Case 2: Parent selected → only that parent's subtypes
      if (options.value.parentId) {
        const inventoryType = filteredInventoryTypesByResourceTypeActivityId.value.find(
          (d) => d.id === options.value.parentId,
        )

        return orderBy(
          inventoryType?.inventorySubTypes
            .filter((d) => d.name?.toLowerCase().includes(searchText))
            .map((l) => ({
              id: l.id,
              title: l.name,
              selectable: disableInventorySubTypes?.value
                ? !disableInventorySubTypes.value.includes(l.id)
                : true,
              expandable: false,
            })) || [],
          [(x) => x.title?.toLowerCase()],
        )
      }

      // Case 3: Search text but no parent → return subtypes if parent OR subtype matches
      const subTypeMatches = filteredInventoryTypesByResourceTypeActivityId.value.flatMap((t) =>
        t.inventorySubTypes
          .filter(
            (st) =>
              st.name?.toLowerCase().includes(searchText) ||
              t.name?.toLowerCase().includes(searchText),
          )
          .map((st) => ({
            id: st.id,
            title: st.name,
            selectable: disableInventorySubTypes?.value
              ? !disableInventorySubTypes.value.includes(st.id)
              : true,
            expandable: false,
            subTitle: t.name,
          })),
      )

      return orderBy(subTypeMatches, [(x) => x.title?.toLowerCase()])
    })

    return {
      items,
      hasNextPage: ref(false),
      fetchNextPage: () => {},
      isFetching,
      isFetched,
      isInitialLoading,
    }
  }

  // Utility function to wait for loading completion
  const waitForDataLoaded = () => {
    return new Promise<void>((resolve) => {
      if (!isInitialLoading.value) {
        resolve()
        return
      }

      const stopWatcher = watch(
        isInitialLoading,
        (loading) => {
          if (!loading) {
            stopWatcher()
            resolve()
          }
        },
        { immediate: true },
      )
    })
  }

  const valueResolver = async (id: string | number | null) => {
    await waitForDataLoaded()

    const numericId = typeof id === 'string' ? parseInt(id, 10) : id

    // Search allInventoryTypes for subtypes that match the id
    if (allInventorySubTypes.value) {
      const matchingSubType = allInventorySubTypes.value.find((subType) => subType.id === numericId)

      if (matchingSubType) {
        return { id, title: matchingSubType.name }
      }
    }

    return { id, title: '' }
  }

  return {
    allInventoryTypes,
    filteredInventoryTypesByResourceTypeActivityId,
    allFilteredInventorySubTypes,
    isFetched,
    isFetching,
    isInitialLoading,
    searchInventories,
    valueResolver,
    waitForDataLoaded,
  }
}
